import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { AnagClientiService } from '@/core/service/anag-clienti.service';
import { AnagFornitoriService } from '@/core/service/anag-fornitori.service';

type Mode = 'new' | 'edit' | 'view';
type Tipo = 'cliente' | 'fornitore';

@Component({
    selector: 'app-anagrafica-form',
    standalone: true,
    providers: [MessageService],
    imports: [CommonModule, ReactiveFormsModule, ButtonModule, InputTextModule, TextareaModule, CheckboxModule, SelectModule, ToastModule],
    templateUrl: './anagrafica-form.component.html',
    styleUrl: './anagrafica-form.component.scss'
})
export class AnagraficaFormComponent implements OnInit {
    tipo: Tipo = 'cliente';
    mode: Mode = 'new';
    saving = false;
    form!: FormGroup;

    readonly countries = [
        { name: 'Italia', code: 'IT' },
        { name: 'Francia', code: 'FR' },
        { name: 'Germania', code: 'DE' },
        { name: 'Spagna', code: 'ES' },
        { name: 'Regno Unito', code: 'GB' },
        { name: 'Stati Uniti', code: 'US' }
    ];

    constructor(
        private fb: FormBuilder,
        private clientiService: AnagClientiService,
        private fornitoriService: AnagFornitoriService,
        private messageService: MessageService,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.tipo = (this.route.snapshot.data['tipo'] as Tipo) ?? 'cliente';
        this.buildForm();

        const id = this.route.snapshot.paramMap.get('id');
        const qpMode = this.route.snapshot.queryParamMap.get('mode');

        if (!id) {
            this.mode = 'new';
        } else {
            this.mode = qpMode === 'view' ? 'view' : 'edit';
            this.loadItem(Number(id));
        }
    }

    get isCliente(): boolean {
        return this.tipo === 'cliente';
    }
    get isNew(): boolean {
        return this.mode === 'new';
    }
    get isReadonly(): boolean {
        return this.mode === 'view';
    }
    get title(): string {
        const t = this.isCliente ? 'cliente' : 'fornitore';
        return this.mode === 'new' ? `Nuovo ${t}` : this.mode === 'view' ? `Dettaglio ${t}` : `Modifica ${t}`;
    }
    get listLabel(): string {
        return this.isCliente ? 'Clienti' : 'Fornitori';
    }

    private get service() {
        return this.isCliente ? this.clientiService : this.fornitoriService;
    }

    private buildForm(): void {
        const common = {
            id: [null],
            ragioneSociale: ['', [Validators.required]],
            partitaIva: [''],
            codiceFiscale: [''],
            indirizzo: [''],
            cap: [''],
            localita: [''],
            paese: [''],
            telefonoAzienda: [''],
            email: ['', [Validators.email]],
            nome: [''],
            cognome: [''],
            cellulare: [''],
            note: ['']
        };

        this.form = this.isCliente
            ? this.fb.group({ ...common, fax: [''], sitoWeb: [''] })
            : this.fb.group({ ...common, flgAttivo: [true] });
    }

    private loadItem(id: number): void {
        this.service.get(id).subscribe({
            next: (row: any) => {
                this.form.reset({ ...row });
                if (this.isReadonly) this.form.disable();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Caricamento non riuscito' })
        });
    }

    salva(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.messageService.add({ severity: 'warn', summary: 'Dati mancanti', detail: 'Compila i campi obbligatori (Ragione sociale).' });
            return;
        }

        this.saving = true;
        const payload = this.form.getRawValue();
        const isNew = !payload.id;
        const req$ = isNew ? this.service.create({ ...payload, id: null } as any) : this.service.update(payload as any);

        req$.subscribe({
            next: (saved: any) => {
                this.saving = false;
                this.messageService.add({ severity: 'success', summary: 'Salvato', detail: `${this.isCliente ? 'Cliente' : 'Fornitore'} ${isNew ? 'creato' : 'aggiornato'} correttamente` });
                // resta sulla pagina; se nuovo passa alla modifica del record creato
                if (this.mode === 'new' && saved?.id != null) {
                    this.router.navigate(['/anagrafica', this.tipo, saved.id], { replaceUrl: true });
                }
            },
            error: (err: any) => {
                this.saving = false;
                console.error(err);
                this.messageService.add({ severity: 'error', summary: 'Errore', detail: `Non è stato possibile ${isNew ? 'creare' : 'aggiornare'} il ${this.isCliente ? 'cliente' : 'fornitore'}` });
            }
        });
    }

    annulla(): void {
        this.router.navigate(['/anagrafica']);
    }
}
